import { CSSProperties, FC, MouseEvent, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import './GroupPage.css';
import { Button, PageHeader } from '../../ui';

export type GroupJump = {
  /** Anchor id and hash suffix, e.g. "events" -> #reports-events. */
  id: string;
  label: string;
};

type GroupPageProps = {
  title: string;
  subtitle: string;
  jumps: GroupJump[];
  /** Hash prefix for section anchors, e.g. "reports" -> #reports-events. */
  hashPrefix: string;
  /** Hash of the section in view, used to mark the jump link. */
  activeHash: string | null;
  /** Section to scroll to on arrival, when the URL named one. */
  scrollTo: string | null;
  /** Tell the app which section the URL should now name. */
  onSectionNavigate: (section: string) => void;
  onExit: () => void;
  /** Rendered beside the sections on a wide screen (the till preview). */
  aside?: ReactNode;
  /**
   * Dashboards need room: Reports deliberately uses more of the viewport than a
   * settings form would, while keeping the same outer alignment.
   */
  wide?: boolean;
  /** Placement only: a page-specific class on the shell root. */
  className?: string;
  children: ReactNode;
};

/** One section of a grouped page. Carries the anchor the jump links target. */
export const GroupSection: FC<{ id: string; hashPrefix: string; label: string; children: ReactNode }> = ({
  id,
  hashPrefix,
  label,
  children,
}) => (
  <section id={hashPrefix + '-' + id} className="groupPageSection" aria-label={label}>
    {children}
  </section>
);

/**
 * One destination, several related sections, one page.
 *
 * Related content of similar scope belongs together, and stacking it with jump
 * links keeps where-you-are and where-you-can-go visible without a second nav
 * bar.
 *
 * Section navigation is handled here rather than left to the browser: following a
 * fragment scrolls every scrollable ancestor, and this shell's ancestors include
 * the document. That dragged the navigation bar off the top of the screen and
 * left the page looking cut off. So the page scrolls its own container by exactly
 * the distance to the section, and the URL is updated through the app's router.
 */
const GroupPage: FC<GroupPageProps> = ({
  title,
  subtitle,
  jumps,
  hashPrefix,
  activeHash,
  scrollTo,
  onSectionNavigate,
  onExit,
  aside,
  wide = false,
  className,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const jumpsRef = useRef<HTMLElement | null>(null);
  // The sticky bar's height, published as a CSS variable so .groupPageSection's
  // scroll-margin-top tracks it. The 64px constant it replaces was a guess: a bar
  // that wraps to two rows is taller than 64 and would land a section under it.
  const [jumpBarHeight, setJumpBarHeight] = useState(0);

  useLayoutEffect(() => {
    const bar = jumpsRef.current;
    if (!bar) return undefined;
    const measure = () => setJumpBarHeight(bar.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  /** Move the page, not the shell, so the target lands below the sticky bar. */
  const scrollToSection = useCallback(
    (id: string) => {
      const container = containerRef.current;
      const target = document.getElementById(hashPrefix + '-' + id);
      if (!container || !target) return;
      const bar = jumpsRef.current ? jumpsRef.current.getBoundingClientRect().height : 0;
      const delta = target.getBoundingClientRect().top - container.getBoundingClientRect().top - bar - 8;
      container.scrollTo({ top: container.scrollTop + delta });
    },
    [hashPrefix]
  );

  useEffect(() => {
    // A deep link lands after mount, and something outside this component (a
    // restored fragment, the browser) may have scrolled the shell first.
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    if (scrollTo) scrollToSection(scrollTo);
  }, [scrollTo, scrollToSection]);

  const jump = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    scrollToSection(id);
    onSectionNavigate(id);
  };

  return (
    <div
      className={className ? 'groupPage ' + className : 'groupPage'}
      ref={containerRef}
      style={{ '--group-jumps-h': jumpBarHeight + 'px' } as CSSProperties}
    >
      <div className={aside || wide ? 'groupPageInner groupPageInnerWide' : 'groupPageInner'}>
        <PageHeader
          className="groupPageHeader"
          title={title}
          description={subtitle}
          action={
            <Button variant="secondary" onClick={onExit}>
              back to till
            </Button>
          }
        />

        <nav className="groupPageJumps" aria-label={'Sections of ' + title} ref={jumpsRef}>
          {jumps.map((entry) => {
            const hash = '#' + hashPrefix + '-' + entry.id;
            const active = activeHash === hash;
            return (
              <a
                key={entry.id}
                className={active ? 'groupPageJump groupPageJumpActive' : 'groupPageJump'}
                href={hash}
                aria-current={active ? 'true' : undefined}
                onClick={(event) => jump(event, entry.id)}
              >
                {entry.label}
              </a>
            );
          })}
        </nav>

        <div className={aside ? 'groupPageBody groupPageBodyWithAside' : 'groupPageBody'}>
          <div className="groupPageSections">{children}</div>
          {aside && (
            <aside className="groupPageAside" aria-label="Preview">
              {aside}
            </aside>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupPage;
